import React, {useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components/native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {Space} from '../../home';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import Animated, {FadeIn} from 'react-native-reanimated';
import {TabKey} from '../../../../utils/enums';
import {DrugItemDetailsType} from '../../../../interfaces/api/tabs';
import GenioLatestNews from '../../../../components/genioLatestNews';
import SkeletonPlaceholder from '../../../../components/skeletonPlaceholder';
import {getDrugDetails} from '../../drugs/actions';
import {DrugsDiseasesDetailsProps} from '../../../../interfaces/screens/drugsDiseasesDetails';
import MainLayout from '../../../mainLayout';
import {toggleSettingsModal} from '../../../../connected-components/settingsModal/actions';
import TextGeneric from '../../../../components/textGeneric';
import {tr} from '../../../../translations';
import {
  cleanAndFormatMarkdown,
  markdownRules,
  markdownStyle,
  openMDLink,
} from '../../../../utils/markdownHelpers';
import {Platform, ScrollView} from 'react-native';
import {getFontFamily} from '../../../../utils/helpers';
import {getByScreenSize, isWeb, wdp} from '../../../../utils/responsive';
import Markdown from '@ronradtke/react-native-markdown-display';
import {Theme} from '../../../../theme/lightTheme';
import {BottomSheetScrollView} from '@gorhom/bottom-sheet';

// Markdown-it instance with HTML parsing enabled
const markdownItInstance = require('markdown-it')({
  html: true,
  breaks: true,
  linkify: true,
});

const Container = styled.View`
  width: 100%;
  height: 100%;
`;
const Title = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s5}px;
  color: ${({theme}) => theme.text.dark};
  padding-vertical: ${({theme}) => theme.text.s3}px;
  padding-horizontal: ${({theme}) => theme.text.s55}px;
  letter-spacing: 0.5px;
  text-align: left;
`;

/**
 * DrugsDiseasesDetails screen component.
 *
 * @param {DrugsDiseasesDetailsProps} props - The screen component props.
 */
const DrugsDiseasesDetails: React.FC<DrugsDiseasesDetailsProps> = ({
  route,
}: DrugsDiseasesDetailsProps) => {
  const theme = useTheme() as Theme;
  const {tabKey, id, title} = route?.params || {};
  const {bottom: bottomInset} = useSafeAreaInsets();
  const {hdp, isMobile} = useResponsiveDimensions();
  const [data, setData] = useState<DrugItemDetailsType[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const initDrugDetails = async () => {
    if (!id) {
      setData([]);
      setIsError(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsError(false);
    const res = await getDrugDetails(id, tabKey === TabKey.Diseases);
    if (!res) {
      setIsError(true);
      setIsLoading(false);
      return;
    }
    setData(res || []);
    setIsLoading(false);
  };

  useEffect(() => {
    initDrugDetails();
  }, [id, tabKey]);

  const BottomSheetScrollViewContainer =
    isWeb && !isMobile ? ScrollView : BottomSheetScrollView;

  const MarkDownDetails = ({text}: {text: string}) => {
    return (
      <BottomSheetScrollViewContainer>
        <Markdown
          markdownit={markdownItInstance}
          rules={markdownRules(theme)}
          style={{
            ...markdownStyle(theme),
            body: {
              color: theme.text.dark,
              fontFamily:
                Platform.OS === 'ios' ? getFontFamily().regular : undefined,
              fontSize:
                Platform.OS === 'ios' ? theme.text.s6 : theme.text.s6 + 1,
              zIndex: 2,
              width: isWeb && !isMobile ? '92%' : wdp(92),
              marginHorizontal: isWeb && !isMobile ? '4%' : wdp(4),
              paddingBottom: isWeb && !isMobile ? hdp(5) : 0,
            },
          }}
          onLinkPress={openMDLink}>
          {cleanAndFormatMarkdown(text || '')}
        </Markdown>
        <Space bottomInset={bottomInset} extraSpace={hdp(5)} />
      </BottomSheetScrollViewContainer>
    );
  };

  const onTopicSelectPress = (item: DrugItemDetailsType) => {
    toggleSettingsModal({
      title: item?.section_name || '',
      component: () => <MarkDownDetails text={item?.value} />,
    });
  };

  const _renderDrugDetailsItem = (item: DrugItemDetailsType, index: number) => {
    return (
      <GenioLatestNews
        key={item?.section_name || index}
        title={item?.section_name || ''}
        onPress={() => {
          onTopicSelectPress?.({...item, index});
        }}
        style={{
          width: '100%',
          marginBottom: getByScreenSize(2, theme.text.s12),
        }}
      />
    );
  };

  return (
    <MainLayout backHeader menuHeader noPadding title={title || ''}>
      <Container>
        <Title
          style={{width: getByScreenSize('100%', '60%'), alignSelf: 'center'}}
          bold>
          {tr('drugsAndDiseases.overview')}
        </Title>
        <Animated.View
          entering={FadeIn}
          style={{
            flex: 1,
            width: getByScreenSize('100%', '60%'),
            paddingBottom: '5%',
            alignSelf: 'center',
          }}>
          {isError
            ? null
            : isLoading
              ? Array(14)
                  .fill({})
                  .map((item, index) => {
                    return (
                      <SkeletonPlaceholder
                        key={index}
                        width={'100%'}
                        height={hdp(10)}
                        marginTop={'1%'}
                        marginRight={'1%'}
                        marginBottom={1}
                        marginLeft={'1%'}
                        borderRadius={8}
                      />
                    );
                  })
              : (data || []).map(_renderDrugDetailsItem)}
        </Animated.View>
        <Space bottomInset={bottomInset} extraSpace={hdp(10)} />
      </Container>
    </MainLayout>
  );
};

export default DrugsDiseasesDetails;
